// components/SEOMeta.tsx
import React, { useEffect } from 'react';
import { SecurityEvent } from '../types.js';

interface SEOMetaProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  author?: string;
  ogType?: string;
  ogImage?: string;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  articleAuthor?: string;
  articleAuthorUrl?: string;
  articleSection?: string;
  articleTags?: string[];
  structuredData?: any;
  noindex?: boolean;
  nofollow?: boolean;
}

export const generateEventSEO = (event: SecurityEvent) => {
  const victimName = event.victim?.name || "Target Organization";
  const attackType = event.attackType || "Cyber Attack";
  const victimNode = event.victim?.node || "critical infrastructure";
  const victimCountry = event.victim?.country || "Global";
  
  const displayYear = event.eventYear < 0 ? `${Math.abs(event.eventYear)} BCE` : event.eventYear;
  
  // Generate OG image URL
  const ogImageUrl = `https://cyberannals.com/api/og/event/${event.id}.png`;
  
  // SEO-optimized title (50-60 chars)
  const title = event.title.length > 60 
    ? `${event.title.substring(0, 57)}... | ${displayYear} Cyber Attack`
    : `${event.title} | ${displayYear} Cyber Attack | Cyber Annals`;
  
  // SEO-optimized description (150-160 chars)
  const description = `Complete analysis of ${attackType} targeting ${victimName}.`;
  
  // Keywords
  const keywords = [
    event.title,
    attackType,
    `${displayYear} cyber attack`,
    `${victimName} breach`,
    `${victimCountry} cybersecurity`,
    event.decade,
    'cybersecurity case study',
    'historical cyber attack',
    'data breach analysis',
    'security incident report'
  ].filter(Boolean).join(', ');
  
  // Main Article structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": event.title,
    "description": description,
    "datePublished": `${event.eventYear > 0 ? event.eventYear : '0001'}-01-01`,
    "dateModified": new Date().toISOString().split('T')[0],
    "author": {
      "@type": "Person",
      "name": "Cyber Annals Researcher",
      "url": "https://cyberannals.com",
      "description": "Cybersecurity historian documenting historical attacks"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Cyber Annals",
      "url": "https://cyberannals.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://cyberannals.com/cyberAnnals_logo.png",
        "width": 600,
        "height": 60
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://cyberannals.com/event/${event.id}`
    },
    "keywords": keywords.split(', ').slice(0, 10),
    "articleSection": victimNode,
    "image": ogImageUrl
  };

  // Breadcrumb structured data for better navigation in search results
  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://cyberannals.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Decades",
        "item": "https://cyberannals.com/decades"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": `${displayYear}`,
        "item": `https://cyberannals.com/year/${event.eventYear}`
      },
      {
        "@type": "ListItem",
        "position": 4,
        "name": event.title,
        "item": `https://cyberannals.com/event/${event.id}`
      }
    ]
  };

  return {
    title,
    description,
    keywords,
    ogType: "article" as const,
    ogImage: ogImageUrl,
    articlePublishedTime: `${event.eventYear > 0 ? event.eventYear : '0001'}-01-01`,
    articleModifiedTime: new Date().toISOString().split('T')[0],
    articleAuthor: "Cyber Annals Researcher",
    articleAuthorUrl: "https://cyberannals.com",
    articleSection: victimNode,
    articleTags: [
      event.attackType,
      event.decade,
      event.victim?.country,
      ...(event.victim?.node ? [event.victim.node] : [])
    ].filter(Boolean),
    structuredData: [structuredData, breadcrumbStructuredData]
  };
};

/**
 * Generate SEO for decade/year pages
 */
export const generateDecadeSEO = (decade: string, count: number) => {
  return {
    title: `${decade} Cyber Security Timeline | ${count} Historical Attacks & Breaches`,
    description: `Complete analysis of cyber security events during ${decade}. Explore ${count} security incidents with detailed case studies.`,
    keywords: `${decade} cybersecurity, ${decade} timeline, historical cyber attacks ${decade}, ${decade} data breaches, cybersecurity evolution ${decade}`,
    ogImage: `https://cyberannals.com/api/og/decade/${encodeURIComponent(decade)}.png`
  };
};

/**
 * Generate SEO for year pages
 */
export const generateYearSEO = (year: number | string, count: number) => {
  const displayYear = typeof year === 'string' && year.includes('BCE') ? year : 
                     Number(year) < 0 ? `${Math.abs(Number(year))} BCE` : year;
  
  return {
    title: `${displayYear} Cyber Security Incidents | Complete Year Analysis`,
    description: `All cyber security events documented in ${displayYear}. ${count} verified incidents.`,
    keywords: `${displayYear} cyber attacks, ${displayYear} data breaches, ${displayYear} security incidents, historical cybersecurity ${displayYear}`,
    ogImage: `https://cyberannals.com/api/og/year/${year}.png`
  };
};

/**
 * Main SEO Component
 */
const SEOMeta: React.FC<SEOMetaProps> = ({ 
  title = "Cyber Security Historical Database | Attack Timeline & Case Studies",
  description = "Comprehensive historical database of cyber security events from ancient cryptography to modern ransomware attacks.",
  keywords = "cybersecurity historical timeline, major cyber attacks database, famous hacking incidents, data breach archive, cyber warfare evolution, ransomware history, nation-state cyber operations",
  canonical = typeof window !== 'undefined' ? window.location.href : '',
  ogType = "website",
  ogImage = "https://cyberannals.com/cyberAnnals_logo.png",
  articlePublishedTime,
  articleModifiedTime,
  articleAuthor = "Cyber Annals Researcher",
  articleAuthorUrl = "https://cyberannals.com",
  articleSection = "Cybersecurity History",
  articleTags = [],
  structuredData,
  noindex = false,
  nofollow = false
}) => {
  useEffect(() => {
    // Update document title
    document.title = title;
    
    // Helper function to update or create meta tags
    const updateMetaTag = (property: string, content: string, isProperty = false, attributeName = 'name') => {
      const attribute = isProperty ? 'property' : attributeName;
      let element = document.querySelector(`meta[${attribute}="${property}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, property);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };


    // Update robots meta
    const robotsContent = `${noindex ? 'noindex' : 'index'}, ${nofollow ? 'nofollow' : 'follow'}`;
    updateMetaTag('robots', robotsContent);

    // Update standard meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    if (articleAuthor) updateMetaTag('author', articleAuthor);
    
    // Open Graph tags

    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:type', ogType, true);
    updateMetaTag('og:url', canonical, true);
    updateMetaTag('og:site_name', 'Cyber Annals', true);
    updateMetaTag('og:locale', 'en_US', true);
    
    // Open Graph Image
    updateMetaTag('og:image', ogImage, true);
    updateMetaTag('og:image:width', '1200', true);
    updateMetaTag('og:image:height', '630', true);
    updateMetaTag('og:image:alt', title, true);
    updateMetaTag('og:image:type', 'image/png', true);
    
    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image', false, 'name');
    updateMetaTag('twitter:title', title, false, 'name');
    updateMetaTag('twitter:description', description, false, 'name');
    updateMetaTag('twitter:image', ogImage, false, 'name');
    updateMetaTag('twitter:site', '@CyberAnnals', false, 'name');
    updateMetaTag('twitter:creator', '@CyberAnnals', false, 'name');
    updateMetaTag('twitter:label1', 'Written by', false, 'name');
    updateMetaTag('twitter:data1', articleAuthor, false, 'name');
    
    if (ogType === 'article') {
      if (articlePublishedTime) updateMetaTag('article:published_time', articlePublishedTime, true);
      if (articleModifiedTime) updateMetaTag('article:modified_time', articleModifiedTime, true);
      if (articleSection) updateMetaTag('article:section', articleSection, true);
      articleTags.forEach(tag => updateMetaTag('article:tag', tag, true));
      if (articleAuthor) updateMetaTag('article:author', articleAuthor, true);
      if (articleAuthorUrl) updateMetaTag('article:author:url', articleAuthorUrl, true);
    }

    // Update canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonical);

    // Add structured data if provided (handles both single and array)
    if (structuredData) {
      const dataArray = Array.isArray(structuredData) ? structuredData : [structuredData];
      dataArray.forEach(data => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.text = JSON.stringify(data);
        document.head.appendChild(script);
      });
    }

    // Cleanup function
    return () => {
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
    };
  }, [
    title, description, keywords, canonical, ogType, ogImage,
    articlePublishedTime, articleModifiedTime, articleAuthor, articleAuthorUrl, articleSection,
    articleTags, structuredData, noindex, nofollow
  ]);
  
  return null;
};

export default SEOMeta;
